'use client'
import s from './Hero.module.scss'
import { useContext, useEffect, useRef, useState } from 'react'
import LazyImage from '@/components/common/LazyImage'
import { Image } from '@/types/common/ImageData'
import { WebContext } from '@/context/webContext'
import LazyVideo from '@/components/common/LazyVideo'

interface ImageProp {
    image: Image | null;
}

interface Hero {
    author: string | null;
    colored: boolean | null;
    color: string | null;
    mediaType: string;
    image: ImageProp | null;
    imageMobile: ImageProp | null;
    video: {
        videoUrl: string | null;
        videoMobileUrl: string | null;
    };
    headerColor: string | null;
}

interface IntroductionProps {
    hero: Hero;
    title: string;
    secondTitle: string | null;
    subtitle: string | null;
    disableProgress?: boolean;
}

export default function HeroComponent({
    hero, title, secondTitle, subtitle, disableProgress = false
}: IntroductionProps) {

    const { colorHeader, setColorHeader, setBackgroundHeader } = useContext(WebContext)

    const heroRef = useRef<HTMLDivElement | null>(null);
    const progressRef = useRef<HTMLDivElement | null>(null);

    const [isMobile, setIsMobile] = useState(false);

    // La barra de progreso se pinta directamente en el DOM (sin estado de React)
    // para que el scroll en mobile no dispare un re-render por evento
    useEffect(() => {
        let frame = 0;

        const update = () => {
            frame = 0;
            if (!progressRef.current) return;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const progress = docHeight > 0 ? Math.min(1, window.scrollY / docHeight) : 0;
            progressRef.current.style.background =
                `linear-gradient(to right, #B7B3A8 ${progress * 100}%, transparent 0)`;
        };

        const handleScroll = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Detectar si es mobile o desktop
    useEffect(() => {
        const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // El Hero está visible
                    if (hero.colored && hero.color) {
                        // Hero de color plano: su texto es negro, así que el header también
                        // (salvo que el editor haya elegido explícitamente otro color)
                        setBackgroundHeader(false);
                        setColorHeader(hero.headerColor && hero.headerColor !== 'white' ? hero.headerColor : 'black');
                    } else {
                        setColorHeader(hero.headerColor ? hero.headerColor : 'white');
                        setBackgroundHeader(false);
                    }
                } else {
                    // El Hero ya no está visible
                    setBackgroundHeader(true);
                    setColorHeader('black');
                }
            },
            { threshold: 0.1 } // Umbral para considerar el Hero visible
        );

        if (heroRef.current) {
            observer.observe(heroRef.current);
        }

        return () => {
            if (heroRef.current) {
                observer.unobserve(heroRef.current);
            }
        };
    }, [hero, setColorHeader, setBackgroundHeader]);

    return (
        <>
            <section className={s.heroComponent}>

                {hero.colored ? (
                    <div ref={heroRef} className={`${s.hero} ${s.black}`} style={{ backgroundColor: `${hero.color ? hero.color : 'transparent'}` }}>
                        <div className={s.heroContent}>
                            <div className={s.heroText}>
                                <h1>{title}{secondTitle && <>: <br></br>{secondTitle}</>}</h1>
                                {hero.image && hero.image.image && <div
                                    className={s.heroImageContain}
                                    style={{ aspectRatio: hero.image.image.metadata.dimensions.width / hero.image.image.metadata.dimensions.height }}>

                                    <LazyImage
                                        src={hero.image.image.imageUrl}
                                        width={hero.image.image.metadata.dimensions.width}
                                        height={hero.image.image.metadata.dimensions.height}
                                        blurDataURL={hero.image.image.ref}
                                        fill={true}
                                        alt={hero.image.image.attribution || ""}
                                        caption={hero.image.image.caption || null}
                                        filename={hero.image.image.filename || ""}
                                    />

                                </div>}
                                <div>
                                    {subtitle && <p className={s.h1}>{subtitle}</p>}
                                    {hero.author && <p className={s.author}><span>Written By</span> <span>{hero.author}</span></p>}
                                </div>
                            </div>
                        </div>
                    </div>
                ) :
                    <div ref={heroRef} className={s.hero}>
                        <div className={s.heroMedia}>

                            {hero.mediaType === 'video' ? (
                                // VIDEO BLOCK
                                isMobile && hero.video?.videoMobileUrl ? (
                                    <LazyVideo
                                        src={hero.video.videoMobileUrl}
                                        autoplay={true}
                                        controls={false}
                                        defaultInView={true}
                                        alt={title || ""}
                                    />
                                ) : hero.video?.videoUrl ? (
                                    <LazyVideo
                                        src={hero.video.videoUrl}
                                        autoplay={true}
                                        controls={false}
                                        defaultInView={true}
                                        alt={title || ""}
                                    />
                                ) : null
                            ) : hero.mediaType === 'image' ? (
                                // IMAGE BLOCK
                                isMobile && hero.imageMobile?.image?.imageUrl ? (
                                    <LazyImage
                                        src={hero.imageMobile.image.imageUrl}
                                        width={0}
                                        height={0}
                                        blurDataURL={hero.imageMobile.image.ref}
                                        fill
                                        alt={hero.imageMobile.image.attribution || ""}
                                        caption={hero.imageMobile.image.caption || null}
                                        filename={hero.imageMobile.image.filename || ""}
                                    />
                                ) : hero.image?.image?.imageUrl ? (
                                    <LazyImage
                                        src={hero.image.image.imageUrl}
                                        width={0}
                                        height={0}
                                        blurDataURL={hero.image.image.ref}
                                        fill
                                        alt={hero.image.image.attribution || ""}
                                        caption={hero.image.image.caption || null}
                                        filename={hero.image.image.filename || ""}
                                    />
                                ) : null
                            ) : null}

                        </div>
                        <div className={s.heroContent} style={{ color: hero.headerColor ? hero.headerColor : 'white' }}>
                            <div className={s.heroText}>

                                <h1>{title}{secondTitle && <>: <br></br>{secondTitle}</>}</h1>
                                <div>
                                    {subtitle && <p className={s.h1}>{subtitle}</p>}
                                    {hero.author && <p className={s.author}><span>Written By</span> <span>{hero.author}</span></p>}
                                </div>
                            </div>
                        </div>
                    </div>
                }

            </section>
            {!disableProgress &&
                <div ref={progressRef} className={s.stickyTitle}>
                    <p>{title}</p>
                </div>
            }
        </>
    )
}
